import * as React from 'react';
import styled from 'styled-components';
import { UserToken } from '../../../../../amorg-common';
import { UserAvatar } from '../../../../../designSystem/avatar/UserAvatar';
import { tablet } from '../../../../../designSystem/BreakPoints';
import { Button } from '../../../../../designSystem/button/Button';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { PopupMenuCustomButton } from '../../../../../designSystem/compositeComponents/PopupMenuCustomButton';
import { Icon, iconName } from '../../../../../designSystem/icons/Icons';
import { DarkLink } from '../../../../../designSystem/Link';
import { Modal } from '../../../../../designSystem/Modal';
import { PageGuideDefinition } from '../../../../../designSystem/PageGuide';
import { EnvironmentBanner } from './EnvironmentBanner';

export type PageTopBarBreadcrumb = {
    text:string,
    url:string,
    leadingIcon?:iconName
};

type TopBarProps = {
	activeUser:UserToken,
	guide?:PageGuideDefinition,
	breadCrumbs?:PageTopBarBreadcrumb[]
};

export function TopBar(props:TopBarProps):JSX.Element
{
    const [showGuide, setShowGuide] = React.useState(false);
    const displayName = `${props.activeUser.firstname} ${props.activeUser.lastname}`.trim();
    const initials = `${props.activeUser.firstname.charAt(0)}${props.activeUser.lastname.charAt(0)}`.toUpperCase();
	const breadCrumbs = props.breadCrumbs || [];

    return (
		<>
			{props.guide &&
				<Modal
					title={props.guide.title}
					showModal={showGuide}
					onCancel={() => setShowGuide(false)}
					leftOffsetPx={0}
					content={props.guide.content} />
			}

			<TopBarWrapper>
			<Topbar>
				<Breadcrumbs aria-label="Brødkrummer">
					{breadCrumbs.map((crumb, index) =>
						<React.Fragment key={`${crumb.url}-${crumb.text}-${index}`}>
							{index > 0 && <Separator>/</Separator>}
							{crumb.url ? (
								<BreadcrumbLink href={crumb.url}>
									{crumb.leadingIcon && <Icon name={crumb.leadingIcon} size="x-small" />}
									<BreadcrumbText>{crumb.text}</BreadcrumbText>
								</BreadcrumbLink>
							) : (
								<CurrentBreadcrumb aria-current="page">{crumb.text}</CurrentBreadcrumb>
							)}
						</React.Fragment>
					)}
				</Breadcrumbs>
				<EnvironmentBanner />
				<Actions>
					{props.guide &&
						<HelpButtonWrapper>
							<Button
								variant="text"
								icon="help"
								size="42px"
								ariaLabel="Åbn hjælp til siden"
								toolTipText="Hjælp"
								tooltipHorizontalAlignment="right"
								onClick={() => setShowGuide(true)} />
						</HelpButtonWrapper>
					}

					<PopupMenuCustomButton
						horizontalAlignment="right"
						hoverEffect="background"
						ariaLabel={`Åbn brugermenu for ${displayName}`}
						buttonContent={
							<UserTriggerContent>
								<UserAvatar
									size="32px"
									hideTooltip
									identifier={props.activeUser.userId}
									user={{displayName, initials}} />
								<UserName>{displayName}</UserName>
								<Icon name="arrows_chevron_chevronDown" size="x-small" />
							</UserTriggerContent>
						}
						entries={[
							{
								type:'menuItem',
								content:<Flex gap={8} alignItems="center"><Icon name="navigation_settings" size="x-small" /><span>Rediger profil</span></Flex>,
								onClick:() => window.location.href = '/profile'
							},
							{
								type:'menuItem',
								content:<Flex gap={8} alignItems="center"><Icon name="logout" size="x-small" /><span>Log ud</span></Flex>,
								onClick:() => window.location.href = '/logout'
							}
						]} />
				</Actions>
			</Topbar>
		    </TopBarWrapper>
        </>
    );
}

const TopBarWrapper = styled.header`
    grid-area: topbar;
    position: sticky;
    top: 0;
    z-index: 100;
    min-width: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background: white;
    border-bottom: 1px solid ${props => props.theme.colors.divider.light};
	height: 48px;

`;

const Topbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width:90%;
	margin: 0 auto;
`;

const Breadcrumbs = styled.nav`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    white-space: nowrap;
	font-size: 14px;
`;

const BreadcrumbLink = styled(DarkLink)`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const BreadcrumbText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CurrentBreadcrumb = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
`;

const Separator = styled.span`
    flex: 0 0 auto;
    color: ${props => props.theme.colors.text.muted};
`;

const Actions = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const HelpButtonWrapper = styled.div`
    & button {
        margin: 0;
    }
`;

const UserTriggerContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const UserName = styled.span`
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: ${tablet}px) {
        display: none;
    }
`;